/**
 * Stummes Schleifen-Video hinter einem Standbild (Hero, Sektion "Im Einsatz").
 *
 * - Die Quelle wird erst geladen, wenn das Element in Sichtweite kommt (data-quelle,
 *   auf dem Telefon data-quelle-mobil).
 * - data-ab="750" haelt das Video auf schmalen Bildschirmen ganz aus: dort bleibt das
 *   Standbild stehen (Datenvolumen und LCP der Startseite).
 * - data-warten wartet, bis das Standbild gezeichnet ist (load + zwei Frames), und laedt
 *   erst dann. Im Hero ist das Standbild das LCP-Element; das Video darf ihm keine
 *   Bandbreite wegnehmen, soll aber nicht auf das load-Ereignis der ganzen Seite warten.
 *   Das Standbild ist das erste Bild des Videos, der Wechsel bleibt unsichtbar.
 * - Ausserhalb der Sicht wird angehalten.
 * - Der Knopf haelt an oder spielt ab (WCAG 2.2.2). Er zeigt "Abspielen", solange das
 *   Video nicht wirklich laeuft: beim Laden, bei reduzierter Bewegung (kein Autostart)
 *   und wenn der Browser den Autostart verweigert (iOS-Stromsparmodus, play() lehnt ab).
 *   So wirkt das Standbild wie ein angehaltenes Video, das man starten kann.
 */
class EratecVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.knopf = this.querySelector('[data-steuerung]');
    if (!this.video || !this.knopf) return;

    const ab = Number(this.dataset.ab || 0);
    if (ab > 0 && !matchMedia(`(min-width: ${ab}px)`).matches) return;

    this.angehalten = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.knopf.hidden = false;
    // Der Knopf folgt dem, was man sieht: laeuft es, haelt er an, sonst spielt er ab.
    this.knopf.addEventListener('click', () => {
      this.angehalten = this.hasAttribute('data-laeuft');
      this.angehalten ? this.stopp() : this.start();
    });

    if (this.hasAttribute('data-warten')) this.#spaeter(() => this.#beobachten());
    else this.#beobachten();
  }

  #spaeter(fn) {
    const bild = this.querySelector('img');
    const gezeichnet = () => requestAnimationFrame(() => requestAnimationFrame(fn));
    if (!bild || bild.complete) gezeichnet();
    else {
      bild.addEventListener('load', gezeichnet, { once: true });
      bild.addEventListener('error', gezeichnet, { once: true });
    }
  }

  #beobachten() {
    if (!this.isConnected) return;
    this.beobachter = new IntersectionObserver(
      ([eintrag]) => {
        if (eintrag.isIntersecting) {
          if (!this.angehalten) this.start();
        } else if (!this.video.paused) {
          this.video.pause();
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.beobachter.observe(this);
  }

  disconnectedCallback() {
    this.beobachter?.disconnect();
  }

  laden() {
    if (this.video.src) return;
    const mobil = matchMedia('(max-width: 749px)').matches;
    this.video.src = (mobil && this.dataset.quelleMobil) || this.dataset.quelle;
  }

  start() {
    this.laden();
    this.video
      .play()
      .then(() => this.zustand(!this.video.paused))
      .catch(() => this.zustand(false));
  }

  stopp() {
    this.video.pause();
    this.zustand(false);
  }

  zustand(laeuft) {
    this.toggleAttribute('data-laeuft', laeuft);
    this.knopf.setAttribute('aria-label', laeuft ? this.knopf.dataset.anhalten : this.knopf.dataset.abspielen);
  }
}

if (!customElements.get('eratec-video')) {
  customElements.define('eratec-video', EratecVideo);
}
